import React from 'react';
import { useLocation } from 'react-router-dom';
import { useGeneralStore } from 'src/store';

import { AnimatedDots } from '@components/shared/animated-dots/AnimatedDots';

import { W2_TID } from '../constants/testids';
import { W2Shell } from '../components/W2Shell';
import { w2GateDecision } from './w2Host';

/**
 * Branded full-page block screen for token-less B2C routes reached on a W2 host.
 * Reuses the W2 branded-error shell (same look as `W2LinkError`), with copy in
 * the W2 error voice: this surface is only reachable through the enrollment link.
 */
export const W2BlockedPage: React.FC = () => (
  <W2Shell>
    <div data-testid={W2_TID.blocked.root} className="flex flex-col gap-4">
      <h1
        data-testid={W2_TID.blocked.title}
        className="text-3xl font-extrabold text-[#1a1a2e] max-sm:text-2xl"
      >
        This page is only available through your enrollment link
      </h1>
      <p className="text-[15px] leading-6 text-[#5a5a70]">
        Open the enrollment link your employer sent you to continue. If you’ve
        already enrolled, sign in from your enrollment link. Need a new link?
        Please contact your employer or benefits administrator.
      </p>
    </div>
  </W2Shell>
);

/**
 * Neutral, unbranded full-page loader shown while the host type is UNRESOLVED.
 * Rendered INSTEAD of the route tree so no blocked B2C route can mount and fire
 * its effects (application reads / draft bootstrap) before the gate resolves
 * (finding #2). Deliberately brand-agnostic — branding may not be known yet.
 */
const W2HostGateLoading: React.FC = () => (
  <div
    data-testid={W2_TID.blocked.loading}
    className="fixed inset-0 z-[999999] flex h-screen w-screen items-center justify-center bg-background"
  >
    <AnimatedDots />
  </div>
);

interface W2HostGateProps {
  children: React.ReactNode;
}

/**
 * Single-purpose route-level gate: on a W2 host (`webClientType` =
 * SmallEmployerTenant), replace every route OUTSIDE the allowlist with the
 * branded block screen, so a W2 employee can only reach the tokenized enrollment
 * link, the auth screens, and the returning self-selection surface — never a B2C
 * surface that could create B2C state (draft applications / subscriptions) on the
 * shared W2 group.
 *
 * A returning W2 browser is locked down IMMEDIATELY from the persisted
 * `webClientType` (never waiting for the fresh fetch), so a blocked B2C route
 * never mounts; while the host type is genuinely unresolved the gate renders a
 * neutral loading shell INSTEAD of the route tree (prevent mount, not hide); and
 * on a branding-fetch failure with an unresolved host it fails CLOSED. Genuine
 * B2C hosts (any resolved non-W2 `webClientType`) pass through untouched. The
 * allowlist + decision live in `w2Host.ts` (pure, unit-tested).
 */
export const W2HostGate: React.FC<W2HostGateProps> = ({ children }) => {
  const { pathname, search } = useLocation();
  const webClientType = useGeneralStore((s) => s.siteConfig?.webClientType);
  const isLoadingConfig = useGeneralStore((s) => s.isLoadingConfig);
  const configLoadFailed = useGeneralStore((s) => s.configLoadFailed);

  const decision = w2GateDecision({
    webClientType,
    isLoadingConfig,
    configLoadFailed,
    pathname,
    search,
  });

  if (decision === 'loading') return <W2HostGateLoading />;
  if (decision === 'block') return <W2BlockedPage />;
  return <>{children}</>;
};
